import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAPI, useGetUser } from 'hooks';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from 'components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Spinner } from 'components/ui/spinner';
import PilgrimStandaloneShell from 'components/shared/PilgrimStandaloneShell';
import { getMembershipRolePresentation, getUserMembershipWriteFields } from 'helpers/userRoles';
import { cn } from 'lib/utils';
import Locales from 'locales';

const UserProfile = () => {
  const copy = Locales.userProfile;
  const { api } = useAPI();
  const queryClient = useQueryClient();
  const { user, memberships, activeOrganizationId, activeRole, isPilgrim, refreshCurrentUser, setCurrentUser } =
    useGetUser();
  const [baseline, setBaseline] = useState(() => ({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
  }));
  const syncedUserId = useRef(user?.id || null);
  const submissionInFlight = useRef(false);
  const form = useForm({
    defaultValues: baseline,
    mode: 'onBlur',
  });
  const values = form.watch();
  const normalizedValues = {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
  };
  const hasRawChanges = values.firstName !== baseline.firstName || values.lastName !== baseline.lastName;
  const hasIdentityChanges =
    normalizedValues.firstName !== baseline.firstName.trim() || normalizedValues.lastName !== baseline.lastName.trim();
  const isValid = Boolean(normalizedValues.firstName && normalizedValues.lastName);
  const isUserUnavailable = !user?.id;

  useEffect(() => {
    if (!user?.id || syncedUserId.current === user.id) return;

    const nextIdentity = {
      firstName: user.first_name || '',
      lastName: user.last_name || '',
    };
    syncedUserId.current = user.id;
    setBaseline(nextIdentity);
    form.reset(nextIdentity);
  }, [form, user?.first_name, user?.id, user?.last_name]);

  const displayMemberships = (memberships || []).flatMap((membership) => {
    const organizationName = typeof membership.organizationName === 'string' ? membership.organizationName.trim() : '';
    const presentation = getMembershipRolePresentation(membership.role);

    if (!organizationName || !presentation) return [];

    return [
      {
        ...membership,
        organizationName,
        type: copy.access_types[presentation.typeKey],
        level: presentation.levelKey ? copy.access_levels[presentation.levelKey] : null,
        isActive:
          Boolean(activeRole) && membership.organizationId === activeOrganizationId && membership.role === activeRole,
      },
    ];
  });
  const { mutate: updateProfile, isLoading } = useMutation({
    mutationFn: (identity) =>
      api.update(
        'user',
        {
          first_name: identity.firstName,
          last_name: identity.lastName,
          email: user.email,
          ...getUserMembershipWriteFields(user),
          is_onboard: user.is_onboard,
        },
        user.id
      ),
    onSuccess: async ({ data }) => {
      // Adopt the successful response immediately. A follow-up GET is useful for
      // canonical server state, but its failure must not turn a completed PUT
      // into an apparent failed save or retain a stale membership version.
      setCurrentUser(data);
      let refreshedUser = data;
      try {
        refreshedUser = (await refreshCurrentUser()) || data;
      } catch {
        refreshedUser = data;
      }
      await queryClient.invalidateQueries({ queryKey: ['user/'] });
      const confirmedIdentity = {
        firstName: refreshedUser.first_name,
        lastName: refreshedUser.last_name,
      };
      setBaseline(confirmedIdentity);
      form.reset(confirmedIdentity);
      toast.success(copy.save_success);
    },
    onError: () => {
      toast.error(copy.save_error);
    },
    onSettled: () => {
      submissionInFlight.current = false;
    },
  });

  const handleSubmit = () => {
    if (submissionInFlight.current || isUserUnavailable || !hasIdentityChanges || !isValid) return;
    submissionInFlight.current = true;
    updateProfile(normalizedValues);
  };

  const content = (
    <main className="min-h-screen bg-[#fafbfc] px-4 py-7 text-[#3d434a] sm:px-7 sm:py-10">
      <div className="mx-auto w-full max-w-[780px]">
        <header className="mb-5 sm:mb-[26px]">
          <h1 className="text-[21px] font-semibold leading-7 tracking-[-0.35px] text-[#25382d] sm:text-2xl sm:leading-[31px] sm:tracking-[-0.55px]">
            {copy.title}
          </h1>
          <p className="mt-1 text-sm leading-[21px] text-[#5f6875] sm:text-[13px] sm:leading-5">{copy.description}</p>
        </header>

        <Card className="mb-4 overflow-hidden rounded-[10px] border-[#e3e8e5] shadow-none sm:mb-5">
          <CardHeader className="px-5 pb-0 pt-4 sm:px-6">
            <CardTitle className="text-base font-semibold leading-[22px] tracking-[-0.16px] text-[#252a30]">
              {copy.personal_information}
            </CardTitle>
          </CardHeader>
          <Form {...form}>
            <form noValidate aria-busy={isLoading} onSubmit={form.handleSubmit(handleSubmit)}>
              <CardContent className="space-y-2.5 px-5 pb-3 pt-3 sm:px-6 sm:pb-2.5">
                <FormField
                  control={form.control}
                  name="firstName"
                  rules={{ validate: (value) => Boolean(value.trim()) || copy.first_names_required }}
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-[13px] font-medium leading-[18px]">
                        {copy.first_names} <span aria-hidden="true">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          autoComplete="given-name"
                          required
                          readOnly={isLoading || isUserUnavailable}
                          className="h-10 rounded-[7px] border-[#d9e2db] px-[11px] text-sm text-[#3d434a] focus-visible:border-hajj-primary focus-visible:ring-2 focus-visible:ring-[rgba(43,123,83,0.10)] focus-visible:ring-offset-0 aria-[invalid=true]:border-[#c77b74] aria-[invalid=true]:focus-visible:border-[#c77b74] aria-[invalid=true]:focus-visible:ring-[rgba(168,62,62,0.08)] read-only:bg-[#f7f9f7]"
                        />
                      </FormControl>
                      <FormMessage className="text-xs font-normal leading-[18px] text-[#a83e3e]" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  rules={{ validate: (value) => Boolean(value.trim()) || copy.last_name_required }}
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-[13px] font-medium leading-[18px]">
                        {copy.last_name} <span aria-hidden="true">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          autoComplete="family-name"
                          required
                          readOnly={isLoading || isUserUnavailable}
                          className="h-10 rounded-[7px] border-[#d9e2db] px-[11px] text-sm text-[#3d434a] focus-visible:border-hajj-primary focus-visible:ring-2 focus-visible:ring-[rgba(43,123,83,0.10)] focus-visible:ring-offset-0 aria-[invalid=true]:border-[#c77b74] aria-[invalid=true]:focus-visible:border-[#c77b74] aria-[invalid=true]:focus-visible:ring-[rgba(168,62,62,0.08)] read-only:bg-[#f7f9f7]"
                        />
                      </FormControl>
                      <FormMessage className="text-xs font-normal leading-[18px] text-[#a83e3e]" />
                    </FormItem>
                  )}
                />
                <dl className="m-0">
                  <dt className="mb-1.5 text-[13px] font-medium leading-[18px]">{copy.email}</dt>
                  <dd className="m-0">
                    <div className="break-all text-sm leading-[21px]">{user?.email || '-'}</div>
                    <p className="mt-1 text-[13px] leading-5 text-[#5f6875]">{copy.email_help}</p>
                  </dd>
                </dl>
              </CardContent>
              <CardFooter className="flex-wrap justify-end gap-2 border-t border-[#f0f2f1] px-5 py-2.5 sm:gap-2.5 sm:px-6 sm:py-2 max-[380px]:flex-col">
                <Button
                  type="button"
                  variant="outline"
                  className="h-[42px] rounded-lg text-sm shadow-none disabled:border-[#e2e7e3] disabled:bg-[#f1f3f2] disabled:text-[#7b867f] disabled:opacity-100 sm:h-[34px] max-[380px]:w-full"
                  disabled={isLoading || isUserUnavailable || !hasRawChanges}
                  onClick={() => form.reset(baseline)}
                >
                  {copy.cancel}
                </Button>
                <Button
                  type="submit"
                  variant="hajj"
                  className={cn(
                    'h-[42px] min-w-0 flex-1 rounded-lg border border-transparent px-[11px] text-sm shadow-none hover:shadow-none disabled:border-[#e2e7e3] disabled:bg-[#f1f3f2] disabled:text-[#7b867f] disabled:opacity-100 sm:h-[34px] sm:min-w-[246px] sm:flex-none max-[380px]:order-first max-[380px]:w-full max-[380px]:flex-none',
                    isLoading && 'disabled:border-hajj-primary disabled:bg-hajj-primary disabled:text-white'
                  )}
                  disabled={isLoading || isUserUnavailable || !hasIdentityChanges || !isValid}
                >
                  {isLoading && <Spinner aria-label={copy.saving} />}
                  {isLoading ? copy.saving : copy.save}
                </Button>
              </CardFooter>
            </form>
          </Form>
        </Card>

        {!isPilgrim && (
          <Card className="overflow-hidden rounded-[10px] border-[#e3e8e5] shadow-none">
            <CardHeader className="px-5 pb-1 pt-4 sm:px-6">
              <CardTitle className="text-base font-semibold leading-[22px] tracking-[-0.16px] text-[#252a30]">
                {displayMemberships.length === 1
                  ? copy.access_singular
                  : displayMemberships.length > 1
                  ? Locales.formatString(copy.access_plural_count, { count: displayMemberships.length })
                  : copy.access_plural}
              </CardTitle>
            </CardHeader>
            {displayMemberships.length > 0 ? (
              <ul className="m-0 list-none px-5 sm:px-6">
                {displayMemberships.map((membership) => (
                  <li
                    key={`${membership.index}-${membership.organizationId || 'organization'}`}
                    data-membership
                    className="flex items-start gap-3 border-b border-[#edf0ee] py-3 last:border-b-0 max-[380px]:flex-col max-[380px]:gap-1.5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="m-0 break-words text-sm font-medium leading-[21px] text-[#252a30]">
                        {membership.organizationName}
                      </p>
                      <p className="mt-[3px] text-[13px] leading-5 text-[#5f6875]">
                        <span className="block break-words">{membership.type}</span>
                        {membership.level && <span className="mt-px block break-words">{membership.level}</span>}
                      </p>
                    </div>
                    {membership.isActive && (
                      <span className="mt-px inline-flex h-[21px] flex-none items-center whitespace-nowrap rounded-[5px] bg-[#eaf6ee] px-[7px] text-[11px] font-medium text-[#35734e]">
                        {copy.active}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="m-0 px-5 py-[18px] text-sm leading-[22px] text-[#5f6875] sm:px-6">{copy.access_empty}</p>
            )}
            <p className="m-0 px-5 pb-3.5 pt-2 text-[13px] leading-5 text-[#5f6875] sm:px-6">{copy.access_help}</p>
          </Card>
        )}
      </div>
    </main>
  );

  return isPilgrim ? <PilgrimStandaloneShell showBackLink>{content}</PilgrimStandaloneShell> : content;
};

export default UserProfile;
